import { useEffect, useState } from "react";
import { useThemeContext } from "../useThemeContext";

type ThemeType = "light" | "dark";

/**
 * Detects the browser's native theme preference.
 * Listens for changes via `prefers-color-scheme` media query.
 *
 * @returns The browser's native theme: "light" | "dark"
 */
const useBrowserThemePreference = (): ThemeType => {
  const [theme, setTheme] = useState<ThemeType>(() => {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent): void => {
      setTheme(e.matches ? "dark" : "light");
    };

    mediaQuery.addEventListener("change", handleChange);
    return (): void => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return theme;
};

/**
 * Gets the resolved color mode by combining user preference with browser theme.
 * - If localThemeColorMode is "auto", returns the browser's preference
 * - Else, returns the user's explicit choice
 *
 * @returns The resolved theme: "light" | "dark"
 */
export const useResolvedTheme = (): ThemeType => {
  const { localThemeColorMode } = useThemeContext();
  const browserTheme = useBrowserThemePreference();

  return localThemeColorMode === "auto" ? browserTheme : localThemeColorMode;
};
